import { useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Modal } from 'antd';
import { useMount } from 'ahooks';
import { find } from 'lodash-es';
import StatusButton from 'components/StatusButton';
import { useAuthorization } from 'components/Authorization';
import { OnOrder } from 'services/api/pay';
import PackageDlgDetail from './PackageDlgDetail';
import { canPayAirwallex } from './Package';
import usePayPackage from '../hooks/usePayPackage';
import CurrencyCheckbox from './CurrencyCheckbox';
import PayTypeCheckbox from './PayTypeCheckbox';
import { EnterpriseTotal } from './EnterpriseTotal';
import { NonEnterpriseTotal, NonEnterpriseTotalProps } from './NonEnterpriseTotal';
import { PackageTimePanel } from './PackageTimePanel';
import { isQuickChat } from 'productLine';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { CompanyAliHelp, Help } from './OrderAI/Help';

export interface PackageProps {
  orderType: Pay.OrderType;
  orgId: number;
  orderedPackages: Pay.OrderedPackage[];
  packageInfo: Pay.PackageInfoV2;
  onOrder: OnOrder;
}

// 升级/续订套餐
function PackageDlg(props: PackageProps) {
  const { onOrder, packageInfo, orderedPackages, orgId, orderType } = props;

  const { t } = useTranslate();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { accid = '' } = payload || {};

  const { packageData, recommendData } = packageInfo;
  const { recommendPackageId, recommendDurationId } = recommendData;

  const {
    // 是否企业套餐
    isEnterprise,
    currenciesOptions,
    availablePayTypes,
    currency,
    setCurrency,
    payType,
    setPayType,
    setIsEnterprise,
    bank,
    setBank,
  } = usePayPackage();

  const language = useSystemLanguageValue();
  const [accountNum, setAccountNum] = useState(0);
  const [durationNum, setDurationNum] = useState<Pay.DurationNum>(0);
  const [canOrder, setCanOrder] = useState(false);
  // 选择的套餐id
  const [checkedPackageId, setCheckedPackageId] = useState<Pay.PackageId | undefined>(
    recommendPackageId,
  );
  const [calculateRst, setCalculateRst] = useState<Pay.CalculatePackagePriceRst | undefined>();

  // 当前生效的套餐
  const curWorkingPackage = useMemo(
    () => orderedPackages.find((v) => v.status === 1),
    [orderedPackages],
  );

  // 总计价格
  const totalPrices: NonEnterpriseTotalProps['totalPrices'] = useMemo(() => {
    return {
      actualPrice: calculateRst?.payPrice ?? 0,
      reducePrice: calculateRst?.reducePrice ?? 0,
    };
  }, [calculateRst]);

  useMount(() => {
    const recommendPackage = find(packageData, ['id', recommendPackageId]);
    if (recommendPackage) {
      setAccountNum(recommendPackage.accountNum);
      const recommendDuration = find(recommendPackage.durationDesc, ['id', recommendDurationId]);
      setDurationNum(recommendDuration?.durationNum ?? 0);
    }
  });

  let orderElement = null;
  if (isEnterprise) {
    // 企业版套餐信息
    orderElement = <EnterpriseTotal currency={currency} />;
  } else if (canOrder) {
    orderElement = (
      <>
        <NonEnterpriseTotal currency={currency} totalPrices={totalPrices}>
          {calculateRst && curWorkingPackage && (
            <PackageTimePanel
              orderType={orderType}
              curWorkingPackage={curWorkingPackage}
              data={calculateRst}
              currency={currency}
              checkedPackageId={checkedPackageId}
              durationNum={durationNum}
            />
          )}
        </NonEnterpriseTotal>
        <OrderButton
          type="primary"
          size="large"
          onClick={() => {
            if (payType === 'airwallex' && !canPayAirwallex()) {
              Modal.info({
                title: t('market.当前客户端版本不支持 Airwallex 支付'),
                content: t('market.请重启客户端，升级至最新版本来完成订购'),
              });

              return Promise.resolve();
            }

            return onOrder({
              idempotent: `${Date.now()}`,
              accid,
              packageType: isQuickChat ? 3 : 1,
              accountNum,
              durationNum,
              discountTotalPrice: `${totalPrices.actualPrice}`,
              orderAction: orderType,
              payType,
              currency,
              packageId: checkedPackageId!,
              language,
              bank,
            });
          }}
        >
          {orderType === 'UPGRADE' ? t('market.立即升级') : t('market.立即续订')}
        </OrderButton>
      </>
    );
  }
  return (
    <Container>
      <Content>
        <PackageDlgDetail
          orderType={orderType}
          orgId={orgId}
          currency={currency}
          orderedPackages={orderedPackages}
          packageInfo={packageInfo}
          checkedPackageId={checkedPackageId}
          curWorkingPackage={curWorkingPackage}
          setAccountNum={setAccountNum}
          setCanOrder={setCanOrder}
          isEnterprise={isEnterprise}
          setIsEnterprise={setIsEnterprise}
          onPackagePriceChange={setCalculateRst}
          onPackageChange={setCheckedPackageId}
          onDurationChange={setDurationNum}
        />
        <CurrencyCheckbox currencies={currenciesOptions} value={currency} onChange={setCurrency} />
        {isEnterprise ? null : (
          <PayTypeCheckbox
            payTypes={availablePayTypes}
            value={payType}
            onChange={setPayType}
            currency={currency}
            bank={bank}
            onBankChange={setBank}
          />
        )}
        {currency === 'CNY' && (
          <div className="mt-[-12px]">
            <span className="mr-[6px]">
              {t('若需要开具公司抬头增值税发票，请使用公司支付宝进行支付')}
            </span>
            <CompanyAliHelp />
            <Help />
          </div>
        )}
        {orderElement}
      </Content>
    </Container>
  );
}

export default PackageDlg;

const Container = styled.div`
  display: flex;
`;

const Content = styled.div`
  flex: 1 0 0;
  /* margin-right: 280px; */
  > * {
    margin-bottom: 20px;
  }
  > h2 {
    margin-bottom: 12px;
    font-size: 20px;
    color: #232426;
  }
`;

const OrderButton = styled(StatusButton)`
  width: 260px;
  &,
  &:hover,
  &:active,
  &:focus {
    background-color: var(--fett-app-primary-color);
    border: none;
  }
`;
